/**
 * Canonical skill-type metadata used across every UI surface that
 * touches `skill.skillType`.
 *
 * <p>Before this module existed, the SKILL_TYPES array was duplicated
 * across SkillCreateSlideout.jsx, AgentsPage.jsx, and SkillCenterPageV2.jsx.
 * Adding a new kind (P0-2's wasm_skill) required hunting through
 * three files and the new kind always missed at least one surface.
 * This module is the single source of truth.</p>
 *
 * <p>Every consumer should import from here and never define its own
 * skill-type list. New kinds get added in ONE place + every UI lights
 * up consistently.</p>
 *
 * <h3>Shape</h3>
 * <ul>
 *   <li><b>id</b> — backend value (matches `skill.skillType`).</li>
 *   <li><b>label</b> — short human-readable name.</li>
 *   <li><b>description</b> — one-line explanation surfaced in the
 *       creation wizard.</li>
 *   <li><b>emoji</b> — v1 page legacy icon (AgentsPage still uses these).</li>
 *   <li><b>tone</b> — v2 design-token tone for Pill / chip styling.</li>
 *   <li><b>colors</b> — v1 AgentsPage SKILL_TYPE_COLORS shape:
 *       {bg, border, text, label}.</li>
 *   <li><b>iconName</b> — lucide-react icon name; consumers import
 *       the icon and resolve by name. Keeps this module free of
 *       React/JSX deps.</li>
 *   <li><b>marketplaceVisible</b> — true when the kind should appear
 *       in the public skill marketplace's KIND_OPTIONS filter.</li>
 *   <li><b>sandboxed</b> — true when execution runs inside an OS-level
 *       sandbox; surfaced as a 🔒 "Sandboxed" pill in lists.</li>
 * </ul>
 */

/** Canonical skill-type registry. Order is the order shown to users. */
export const SKILL_TYPES = [
  {
    id: 'standard',
    label: 'Standard',
    description: 'Traditional API/handler skill (default).',
    emoji: '⚡',
    tone: 'cyan',
    colors: { bg: '#f5f5f4', border: '#d6d3d1', text: '#1c1917', label: 'Standard' },
    iconName: 'Sparkles',
    marketplaceVisible: false,
    sandboxed: false,
  },
  {
    id: 'workflow_skill',
    label: 'Workflow',
    description: 'Linked to a canvas workflow (workflowId on params).',
    emoji: '🔀',
    tone: 'trust',
    colors: { bg: '#f5f5f4', border: '#d6d3d1', text: '#1e40af', label: 'Workflow' },
    iconName: 'Workflow',
    marketplaceVisible: true,
    sandboxed: false,
  },
  {
    id: 'connector_skill',
    label: 'Connector',
    description: 'Auto-generated from a connector\'s available actions.',
    emoji: '🔌',
    tone: 'cyan',
    colors: { bg: '#e3f4f1', border: '#a5d8d0', text: '#065f46', label: 'Connector' },
    iconName: 'Database',
    marketplaceVisible: true,
    sandboxed: false,
  },
  {
    id: 'policy_skill',
    label: 'Policy',
    description: 'RAG-style — references uploaded policy documents for enforcement.',
    emoji: '📋',
    tone: 'warning',
    colors: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', label: 'Policy' },
    iconName: 'FileText',
    marketplaceVisible: false,
    sandboxed: false,
  },
  /* P0-2 — Wasm-sandboxed skill. Executes inside the Chicory pure-Java
   * WebAssembly runtime with hard memory + wall-clock caps and a curated
   * host-import set (P0-2 Sprint 2). Surfaces with a 🔒 "Sandboxed" pill
   * in lists so operators can see at a glance which skills run third-
   * party code under isolation. */
  {
    id: 'wasm_skill',
    label: 'Wasm sandbox',
    description: 'Sandboxed third-party code in the Chicory WebAssembly VM (memory + time capped, host imports opt-in).',
    emoji: '🔒',
    tone: 'success',
    colors: { bg: '#dcfce7', border: '#86efac', text: '#166534', label: 'Wasm' },
    iconName: 'ShieldCheck',
    marketplaceVisible: false,
    sandboxed: true,
  },
];

/** Lookup helpers — every consumer should reach for these instead of
 *  hand-rolling array.find / dictionary access. */
export const SKILL_TYPE_BY_ID = SKILL_TYPES.reduce(
  (acc, t) => { acc[t.id] = t; return acc; },
  Object.create(null),
);

/** Used by AgentsPage.jsx for the v1 type-counts + colour-chip lookup. */
export const SKILL_TYPE_COLORS = SKILL_TYPES.reduce(
  (acc, t) => { acc[t.id] = t.colors; return acc; },
  Object.create(null),
);

/** Used by SkillCenterPageV2.jsx for the marketplace filter — only the
 *  kinds that make sense as discoverable / browsable. */
export const MARKETPLACE_KIND_OPTIONS = SKILL_TYPES
  .filter((t) => t.marketplaceVisible)
  .map((t) => ({ key: t.id, label: t.label, tone: t.tone }));

/** Resolve a kind id to its metadata, falling back to the standard
 *  entry when the id is unknown (defensive — older skill rows may
 *  carry kinds we no longer surface). */
export function skillTypeOf(id) {
  return SKILL_TYPE_BY_ID[id] || SKILL_TYPE_BY_ID.standard;
}

/**
 * The skill types that make up an ExteLet's protection, as opposed to the
 * vendor tools it calls.
 *
 * <p>`external_policy_proxy` is created by the backend bridge, one per mapped
 * capability, and is what protection binds to; it is deliberately absent from
 * SKILL_TYPES above, because that registry feeds the create pickers and no
 * user may author this type. `policy_skill` is the hand-authored protection
 * policy a tenant adds from the Protect panel.</p>
 *
 * <p>This is the same pair the backend's
 * `ExternalAgentPolicyBridge.hasMaterializedPolicySkillRules` treats as the
 * protection posture, so a surface asking "has it been reviewed" selects the
 * same rows as the gate asking "is it in force".</p>
 */
export const EXTELET_PROTECTION_SKILL_TYPES = Object.freeze([
  'external_policy_proxy',
  'policy_skill',
]);

/** True iff this skill row is part of an ExteLet's protection rather than one
 *  of the vendor tools it may call. Every ExteLet surface that lists
 *  protection skills reaches for this instead of filtering inline. */
export function isExteletProtectionSkill(skill) {
  return EXTELET_PROTECTION_SKILL_TYPES.includes(skill?.skillType);
}

/** True iff the given skill kind executes under an OS-level sandbox.
 *  UI surfaces use this to mount a 🔒 "Sandboxed" pill. */
export function isSandboxedKind(id) {
  return !!SKILL_TYPE_BY_ID[id]?.sandboxed;
}
